import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type {
  QuoteData,
  QuoteResponse,
  CarBrand,
  CarModel,
  CarVersion,
  PersonalData,
} from '@/types/quote.types';

export const useQuoteStore = defineStore('quote', () => {
  const currentStep = ref(1);
  const quoteData = ref<QuoteData>({
    origenUso: null,
    subOption: null,
    year: null,
    brand: null,
    model: null,
    version: null,
    type: null,
    postalCode: null,
    personalData: null,
  });
  const quoteResponse = ref<QuoteResponse | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const totalSteps = 4;

  const canGoNext = computed(() => {
    switch (currentStep.value) {
      case 1:
        return quoteData.value.origenUso !== null && quoteData.value.subOption !== null;
      case 2:
        return (
          quoteData.value.year !== null &&
          quoteData.value.brand !== null &&
          quoteData.value.type !== null &&
          quoteData.value.model !== null
        );
      case 3:
        return !!(
          quoteData.value.personalData?.fullName &&
          quoteData.value.personalData?.gender &&
          quoteData.value.personalData?.postalCode &&
          quoteData.value.personalData?.birthDate &&
          quoteData.value.personalData?.email &&
          quoteData.value.personalData?.whatsapp
        );
      case 4:
        return true; // Coberturas y resumen siempre permiten avanzar
      default:
        return false;
    }
  });

  const canGoBack = computed(() => currentStep.value > 1);

  const setOrigenUso = (origenUso: string) => {
    quoteData.value.origenUso = origenUso;
    quoteData.value.subOption = null; // Resetear sub-opción al cambiar origen
  };

  const setSubOption = (subOption: string) => {
    quoteData.value.subOption = subOption;
  };

  const setYear = (year: number) => {
    quoteData.value.year = year;
    quoteData.value.brand = null;
    quoteData.value.model = null;
    quoteData.value.version = null;
  };

  const setBrand = (brand: CarBrand) => {
    quoteData.value.brand = brand;
    quoteData.value.model = null;
    quoteData.value.version = null;
  };

  const setModel = (model: CarModel) => {
    quoteData.value.model = model;
    // El id del modelo es el Key (clave AMIS) que necesitamos para Clave_Qualitas
    quoteData.value.amis = model.id;
    quoteData.value.version = null;
  };

  const setVersion = (version: CarVersion) => {
    quoteData.value.version = version;
  };

  const setPersonalData = (personalData: PersonalData) => {
    quoteData.value.personalData = personalData;
  };

  const nextStep = () => {
    // Verificar si el paso actual está completo antes de avanzar
    const isCurrentStepValid = canGoNext.value;
    
    if (isCurrentStepValid && currentStep.value < totalSteps) {
      currentStep.value += 1;
      error.value = null;
    }
  };
  
  const canGoNextFromStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return quoteData.value.origenUso !== null && quoteData.value.subOption !== null;
      case 2:
        return (
          quoteData.value.year !== null &&
          quoteData.value.brand !== null &&
          quoteData.value.type !== null &&
          quoteData.value.model !== null &&
          quoteData.value.postalCode !== null &&
          quoteData.value.postalCode !== ''
        );
      case 3:
        return !!(
          quoteData.value.personalData?.fullName &&
          quoteData.value.personalData?.gender &&
          quoteData.value.personalData?.postalCode &&
          quoteData.value.personalData?.birthDate &&
          quoteData.value.personalData?.email &&
          quoteData.value.personalData?.whatsapp
        );
      case 4:
        return true;
      default:
        return false;
    }
  };

  const previousStep = () => {
    if (canGoNext.value && currentStep.value > 1) {
      currentStep.value -= 1;
      error.value = null;
    }
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= totalSteps) {
      currentStep.value = step;
      error.value = null;
    }
  };

  const setQuoteResponse = (response: QuoteResponse) => {
    quoteResponse.value = response;
    isLoading.value = false;
    error.value = null;
  };

  const setLoading = (loading: boolean) => {
    isLoading.value = loading;
  };

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage;
    isLoading.value = false;
  };

  const reset = () => {
    currentStep.value = 1;
    quoteData.value = {
      origenUso: null,
      subOption: null,
      year: null,
      brand: null,
      model: null,
      version: null,
      type: null,
      amis: undefined,
      serie: undefined,
      salvageUnit: undefined,
      postalCode: null,
      personalData: null,
      cotizacionData: undefined,
    };
    quoteResponse.value = null;
    isLoading.value = false;
    error.value = null;
  };

  const submitQuote = async () => {
    setLoading(true);
    setError(null);
    try {
      const { generateCotizacionAutosDetalle } = await import('@/services/api');
      const cotizacion = await generateCotizacionAutosDetalle(quoteData.value, false);
      
      // Guardar el ID de la cotización generada en el store
      if (cotizacion.Id_Cotizacion_Autos_Detalle) {
        if (!quoteData.value.cotizacionData) {
          quoteData.value.cotizacionData = {};
        }
        quoteData.value.cotizacionData.idCotizacionAutosDetalle = cotizacion.Id_Cotizacion_Autos_Detalle;
      }
      
      if (cotizacion.Id_Cotizacion) {
        if (!quoteData.value.cotizacionData) {
          quoteData.value.cotizacionData = {};
        }
        quoteData.value.cotizacionData.idCotizacion = cotizacion.Id_Cotizacion;
      }
      
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al enviar la cotización');
      setLoading(false);
    }
  };

  return {
    currentStep,
    quoteData,
    quoteResponse,
    isLoading,
    error,
    totalSteps,
    canGoNext,
    canGoBack,
    canGoNextFromStep,
    setOrigenUso,
    setSubOption,
    setYear,
    setBrand,
    setModel,
    setVersion,
    setPersonalData,
    nextStep,
    previousStep,
    goToStep,
    setQuoteResponse,
    setLoading,
    setError,
    reset,
    submitQuote,
  };
});
